import { useCallback, useEffect, useRef, useState } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import {
	FulfillmentOrderStatus,
	useFulfillmentOrderCancellationStatusQuery,
	useRequestFulfillmentOrderCancellationMutation,
} from '../graphql'

// Numina's expected turnaround is ~1s, so we poll fast initially to keep the
// happy path feeling near-synchronous, then back off to a slow cadence (and
// surface a non-blocking warning) if it hasn't resolved within the fast window.
export const FAST_POLL_MS = 750
export const SLOW_POLL_MS = 4000
export const FAST_TIMEOUT_MS = 15000

export type CancellationPhase = 'idle' | 'requesting' | 'polling' | 'succeeded' | 'failed'

type UseFulfillmentOrderCancellationParams = {
	fulfillmentOrderId: string,
	// Whether to actually fetch/poll the FO. When the rollout flag is off we skip
	// the query entirely so users outside the rollout incur no extra requests.
	enabled: boolean,
	onSucceeded?: () => void,
	onFailed?: ( message: string | null | undefined ) => void,
}

/**
 * Drives the asynchronous, Numina-coordinated fulfillment-order cancellation
 * flow for a single FO:
 *
 *   request → (already CANCELLED ⇒ success, no poll)
 *           → (cancellationFailed ⇒ failed)
 *           → CANCELLATION_REQUESTED ⇒ poll fast, then slow + warn past timeout
 *           → CANCELLED ⇒ success | cancellationFailed ⇒ failed
 *
 * Reads live status from the standalone `fulfillmentOrder` query. The status
 * badge cell reads the same query (Apollo dedupes by id), so it reflects poll
 * updates without extra requests. `cancellationFailed` is authoritative for the
 * failed outcome — we do not infer failure from status alone.
 */
export function useFulfillmentOrderCancellation( {
	fulfillmentOrderId,
	enabled,
	onSucceeded,
	onFailed,
}: UseFulfillmentOrderCancellationParams ) {
	const [ phase, setPhase ] = useState<CancellationPhase>( 'idle' )
	const [ tookTooLong, setTookTooLong ] = useState( false )
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null )

	// Keep the latest callbacks in refs so the resolution logic doesn't churn on
	// every parent render or read stale closures.
	const onSucceededRef = useRef( onSucceeded )
	const onFailedRef = useRef( onFailed )
	onSucceededRef.current = onSucceeded
	onFailedRef.current = onFailed

	const { data, startPolling, stopPolling } = useFulfillmentOrderCancellationStatusQuery( {
		variables: { fulfillmentOrderId },
		skip: !enabled,
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	} )

	const fo = data?.fulfillmentOrder
	const status = fo?.status

	const clearFastTimeout = useCallback( () => {
		if ( timeoutRef.current ) {
			clearTimeout( timeoutRef.current )
			timeoutRef.current = null
		}
	}, [] )

	const stop = useCallback( () => {
		stopPolling()
		clearFastTimeout()
	}, [ stopPolling, clearFastTimeout ] )

	const [ requestMutation ] = useRequestFulfillmentOrderCancellationMutation()

	const submit = useCallback( async() => {
		setPhase( 'requesting' )
		setTookTooLong( false )
		heapEvent( 'Inventory - Stock Request - Sourcing Drill-down - Cancel FO', { fulfillment_order_id: fulfillmentOrderId } )
		try {
			const { data: mutData } = await requestMutation( { variables: { input: { fulfillmentOrderId } } } )
			const result = mutData?.requestFulfillmentOrderCancellation
			const resultFo = result?.fulfillmentOrder

			if ( !result?.success ) {
				setPhase( 'failed' )
				onFailedRef.current?.( result?.message )
				return
			}

			// The mutation can already be terminal: an idempotent re-request returns
			// CANCELLED immediately — treat as success and do not poll.
			if ( resultFo?.status === FulfillmentOrderStatus.Cancelled ) {
				setPhase( 'succeeded' )
				onSucceededRef.current?.()
				return
			}

			// Failure is authoritative on cancellationFailed.
			if ( resultFo?.cancellationFailed ) {
				setPhase( 'failed' )
				onFailedRef.current?.( resultFo.cancellationFailureMessage )
				return
			}

			// Otherwise the FO is CANCELLATION_REQUESTED — poll for resolution.
			setPhase( 'polling' )
			startPolling( FAST_POLL_MS )
			clearFastTimeout()
			timeoutRef.current = setTimeout( () => {
				setTookTooLong( true )
				startPolling( SLOW_POLL_MS )
			}, FAST_TIMEOUT_MS )
		} catch ( err ) {
			setPhase( 'failed' )
			onFailedRef.current?.( err instanceof Error ? err.message : null )
		}
	}, [ fulfillmentOrderId, requestMutation, startPolling, clearFastTimeout ] )

	// Resolve the outcome as polled status arrives. cancellationFailed is the
	// source of truth for failure (status will have moved to
	// SUBMITTED_FOR_FULFILLMENT or COMPLETED).
	useEffect( () => {
		if ( phase !== 'polling' || !fo ) {
			return
		}
		if ( fo.cancellationFailed ) {
			setPhase( 'failed' )
			stop()
			onFailedRef.current?.( fo.cancellationFailureMessage )
			return
		}
		if ( fo.status === FulfillmentOrderStatus.Cancelled ) {
			setPhase( 'succeeded' )
			stop()
			onSucceededRef.current?.()
		}
	}, [ phase, fo, stop ] )

	// Auto-resume: if the FO is already CANCELLATION_REQUESTED on load (e.g. the
	// user navigated away and back mid-cancel), quietly resume polling at the
	// reduced cadence so it self-resolves without another user action.
	useEffect( () => {
		if ( phase === 'idle' && status === FulfillmentOrderStatus.CancellationRequested ) {
			setPhase( 'polling' )
			startPolling( SLOW_POLL_MS )
		}
	}, [ phase, status, startPolling ] )

	// Tear down polling/timers on unmount.
	useEffect( () => () => stop(), [ stop ] )

	return {
		status,
		cancellationFailed: fo?.cancellationFailed ?? false,
		cancellationFailureMessage: fo?.cancellationFailureMessage ?? null,
		phase,
		tookTooLong,
		isInProgress: status === FulfillmentOrderStatus.CancellationRequested,
		submit,
	}
}
